// OP-3605 — Posição de estoque por praça (primeira tela do módulo; ESL1).
// Os números de cada linha vêm da mesma consulta do backend (físico,
// disponível, fora da base, reservado, comprometido, crítico).
import { FC, useEffect, useMemo, useState } from 'react'
import { Navigate } from 'react-router-dom'
import { useEstoqueCategorias, useEstoqueLocais, useEstoquePosicao } from '../../../../hooks/estoque'
import { PosicaoFilters, PosicaoRow } from '../../../../types/estoque'
import {
  BD, Button, CaixaLista, Card, Field, FiltersRow, Input, ListaCartoes, PageSubtitle, PageTitle, Pill,
  Select, Spinner, Table, TabelaWrap,
} from '../../styles'
import { plural } from '../formato'
import { rotuloCategoria } from '../Catalogo/regras'
import { ATRASO_DA_BUSCA_MS, buscaPendente, filtrosNaHora } from './regras'
import { GLOSSARIO } from '../glossario'
import { AjudaGlossario } from '../AjudaGlossario'
import { LOCAL_EXECUTION } from '../../../../utils/featureFlag'

const FILTERS_KEY = 'estoque.posicao.filtros'
/**
 * OP-3619 — severidade é âmbar; o vermelho da marca fica para ação e para o
 * número negativo, que é erro e não aviso.
 */
const AMBAR_FUNDO = '#FEF3C7'
const AMBAR_TEXTO = '#92400E'

const TERMOS = ['fisico', 'disponivel', 'reservado', 'comprometido', 'foraDaBase', 'critico'] as const

const loadFilters = (): PosicaoFilters | null => {
  try {
    const raw = sessionStorage.getItem(FILTERS_KEY)
    return raw ? (JSON.parse(raw) as PosicaoFilters) : null
  } catch {
    return null
  }
}

const COLS = 8

export const EstoquePosicao: FC = () => {
  const inicial = useMemo<PosicaoFilters>(() => loadFilters() ?? { page: 1 }, [])
  const [draft, setDraft] = useState<PosicaoFilters>(inicial)
  const [applied, setApplied] = useState<PosicaoFilters>(inicial)
  const { locais, isLoading: locaisCarregando, isError: locaisErro, isUnauthorized: locaisSemSessao, mutate: recarregarLocais } = useEstoqueLocais()
  const semPracaAutorizada = !locaisCarregando && !locaisErro && !locais.some((l) => l.noEscopo)
  const { rows, total, page, lastPage, isLoading, isError, isUnauthorized } = useEstoquePosicao(applied)
  const categoriasQ = useEstoqueCategorias()

  // Sem praça escolhida, a primeira do escopo: a tela nunca abre vazia por
  // falta de seleção, e quem é restrito cai na própria praça.
  useEffect(() => {
    if (!applied.localId && locais.length) {
      const primeira = locais.find((l) => l.noEscopo)
      if (!primeira) return
      setDraft((d) => ({ ...d, localId: primeira.id }))
      setApplied((a) => ({ ...a, localId: primeira.id, page: 1 }))
    }
  }, [locais, applied.localId])

  useEffect(() => {
    try {
      sessionStorage.setItem(FILTERS_KEY, JSON.stringify(applied))
    } catch {
      /* sessionStorage indisponível — ignora */
    }
  }, [applied])

  useEffect(() => {
    if (!buscaPendente(draft, applied)) return
    const t = setTimeout(() => setApplied((a) => ({ ...a, busca: draft.busca, page: 1 })), ATRASO_DA_BUSCA_MS)
    return () => clearTimeout(t)
  }, [draft, applied])

  const aplicar = () => setApplied({ ...draft, page: 1 })
  const escolherNaHora = (patch: Partial<PosicaoFilters>) => {
    const r = filtrosNaHora(draft, patch)
    setDraft(r.draft)
    setApplied(r.applied)
  }
  const pracaAplicada = locais.find((l) => String(l.id) === String(applied.localId ?? ''))
  const irPagina = (p: number) => setApplied((a) => ({ ...a, page: p }))
  const paginaAtual = page ?? 1

  if ((isUnauthorized || locaisSemSessao) && !LOCAL_EXECUTION) {
    return <Navigate to="/auth" replace />
  }

  // Sem a lista de praças a posição nem é consultada — dizer "0 itens" aqui
  // seria mentir. Cada estado tem a própria mensagem.
  if (locaisCarregando) {
    return (
      <>
        <PageTitle>Posição de estoque</PageTitle>
        <Card><div style={{ textAlign: 'center', padding: 24 }}><Spinner /></div></Card>
      </>
    )
  }
  if (locaisErro) {
    return (
      <>
        <PageTitle>Posição de estoque</PageTitle>
        <Card>
          <div style={{ textAlign: 'center', padding: 24, color: BD.red }}>
            Não foi possível carregar as praças.
            <div style={{ marginTop: 12 }}><Button $variant="secondary" onClick={() => recarregarLocais()}>Tentar de novo</Button></div>
          </div>
        </Card>
      </>
    )
  }
  if (semPracaAutorizada) {
    return (
      <>
        <PageTitle>Posição de estoque</PageTitle>
        <Card>
          <div style={{ textAlign: 'center', padding: 24, color: BD.grayText }}>
            Nenhuma praça autorizada para o seu usuário. Peça ao administrador para vincular a sua praça no painel.
          </div>
        </Card>
      </>
    )
  }

  const cartao = (r: PosicaoRow) => (
    <Card key={r.itemId} style={{ padding: 14, borderColor: r.critico ? '#F59E0B' : undefined }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        <b style={{ flexGrow: 1, fontSize: 16 }}>{r.nome}</b>
        {r.critico && <Pill $bg={AMBAR_FUNDO} $color={AMBAR_TEXTO}>crítico</Pill>}
      </div>
      <div style={{ marginTop: 2, fontSize: 13, color: BD.grayText }}>
        {r.codigo ?? '—'} · {r.controle === 'SERIAL' ? 'peça' : (r.unidadeMedida ?? 'un')}
        {r.estoqueMinimo != null ? ` · mínimo ${r.estoqueMinimo}` : ''}
      </div>
      <div style={{ marginTop: 12, display: 'flex', gap: 12 }}>
        <div style={{ flex: '1 1 0' }}>
          <div style={{ fontFamily: 'monospace', fontSize: 22, fontWeight: 700 }}>{r.fisico}</div>
          <div style={{ fontSize: 11, color: BD.grayText }}>físico</div>
        </div>
        <div style={{ flex: '1 1 0' }}>
          <div style={{ fontFamily: 'monospace', fontSize: 22, fontWeight: 700, color: Number(r.disponivel) < 0 ? BD.red : undefined }}>{r.disponivel}</div>
          <div style={{ fontSize: 11, color: BD.grayText }}>disponível</div>
        </div>
        <div style={{ flex: '1 1 0' }}>
          <div style={{ fontFamily: 'monospace', fontSize: 22, fontWeight: 700 }}>{r.reservado}</div>
          <div style={{ fontSize: 11, color: BD.grayText }}>reservado</div>
        </div>
      </div>
      {(Number(r.comprometido) > 0 || Number(r.foraDaBase) > 0 || Number(r.extraviadas) > 0) && (
        <div style={{ marginTop: 10, paddingTop: 10, borderTop: `1px solid ${BD.grayBorder}`, fontSize: 13, color: BD.grayText }}>
          {Number(r.comprometido) > 0 ? `${r.comprometido} comprometido` : ''}
          {Number(r.comprometido) > 0 && Number(r.foraDaBase) > 0 ? ' · ' : ''}
          {Number(r.foraDaBase) > 0 ? `${r.foraDaBase} fora da base` : ''}
          {Number(r.extraviadas) > 0 && (
            <Pill $bg={AMBAR_FUNDO} $color={AMBAR_TEXTO} style={{ marginLeft: 6 }}>
              {plural(Number(r.extraviadas), 'extraviada', 'extraviadas')}
            </Pill>
          )}
        </div>
      )}
    </Card>
  )

  const linha = (r: PosicaoRow) => (
    <tr key={r.itemId} style={{ background: r.critico ? AMBAR_FUNDO : undefined }}>
      <td>
        <div style={{ fontWeight: 600 }}>
          {r.nome}
          {r.critico && <Pill $bg={AMBAR_FUNDO} $color={AMBAR_TEXTO} style={{ marginLeft: 6 }}>crítico</Pill>}
        </div>
        <div style={{ fontSize: 12, color: BD.grayText }}>{r.codigo ?? '—'} · {r.categoria}</div>
      </td>
      <td>{r.controle === 'SERIAL' ? 'peça' : (r.unidadeMedida ?? 'un')}</td>
      <td style={{ textAlign: 'right' }}>{r.fisico}</td>
      <td style={{ textAlign: 'right', fontWeight: 600, color: Number(r.disponivel) < 0 ? BD.red : undefined }}>{r.disponivel}</td>
      <td style={{ textAlign: 'right' }} title={Number(r.reservadoTransferencias) > 0 ? `${Number(r.reservadoPedidos) || 0} em pedidos · ${Number(r.reservadoTransferencias)} em transferências` : undefined}>{r.reservado}</td>
      <td style={{ textAlign: 'right' }}>{r.comprometido}</td>
      <td style={{ textAlign: 'right', whiteSpace: 'nowrap' }}>
        {r.foraDaBase}
        {Number(r.extraviadas) > 0 && <Pill $bg="#FEF3C7" $color="#92400E" style={{ marginLeft: 6 }}>{plural(Number(r.extraviadas), 'extraviada', 'extraviadas')}</Pill>}
      </td>
      <td style={{ textAlign: 'right' }}>{r.estoqueMinimo ?? '—'}</td>
    </tr>
  )

  return (
    <>
      <PageTitle>Posição de estoque</PageTitle>
      <PageSubtitle>O que existe, o que está livre e o que já tem dono, por praça</PageSubtitle>

      <FiltersRow>
        <Field>
          <label>Praça</label>
          <Select value={draft.localId ?? ''} onChange={(e) => escolherNaHora({ localId: e.target.value })}>
            {locais.map((l) => (
              <option key={l.id} value={l.id} disabled={!l.noEscopo}>{l.nome}</option>
            ))}
          </Select>
        </Field>
        <Field>
          <label>Item</label>
          <Input
            value={draft.busca ?? ''}
            placeholder="nome ou código"
            onChange={(e) => setDraft((d) => ({ ...d, busca: e.target.value }))}
            onKeyDown={(e) => e.key === 'Enter' && aplicar()}
          />
        </Field>
        <Field>
          <label>Categoria</label>
          <Select value={draft.categoriaId ?? ''} onChange={(e) => escolherNaHora({ categoriaId: e.target.value })}>
            <option value="">{categoriasQ.isError ? 'todas (lista indisponível)' : 'todas'}</option>
            {categoriasQ.data.map((c) => <option key={c.id} value={c.id}>{rotuloCategoria(c)}</option>)}
          </Select>
        </Field>
        <Field>
          <label>Somente críticos</label>
          <Select value={draft.apenasCriticos ?? ''} onChange={(e) => escolherNaHora({ apenasCriticos: e.target.value as '1' | '' })}>
            <option value="">não</option>
            <option value="1">sim</option>
          </Select>
        </Field>
        <div style={{ display: 'flex', alignItems: 'flex-end' }}>
          <Button $variant="secondary" onClick={aplicar}>Buscar</Button>
        </div>
      </FiltersRow>

      <CaixaLista>
        <div style={{ padding: '8px 12px', fontSize: 13, color: BD.grayText }}>
          {isLoading ? 'Carregando…' : `${plural(total, 'item', 'itens')}${pracaAplicada ? ` em ${pracaAplicada.nome}` : ''}`}
        </div>
        <TabelaWrap>
          <Table>
            <thead>
              <tr>
                <th>Item</th>
                <th>Unidade</th>
                <th style={{ textAlign: 'right' }} title={GLOSSARIO.fisico.texto}>Físico</th>
                <th style={{ textAlign: 'right' }} title={GLOSSARIO.disponivel.texto}>Disponível</th>
                <th style={{ textAlign: 'right' }} title={GLOSSARIO.reservado.texto}>Reservado</th>
                <th style={{ textAlign: 'right' }} title={GLOSSARIO.comprometido.texto}>Comprometido</th>
                <th style={{ textAlign: 'right' }} title={GLOSSARIO.foraDaBase.texto}>Fora da base</th>
                <th style={{ textAlign: 'right' }}>Mínimo</th>
              </tr>
            </thead>
            <tbody>
              {isLoading && (
                <tr><td colSpan={COLS} style={{ textAlign: 'center', padding: 24 }}><Spinner /></td></tr>
              )}
              {!isLoading && isError && (
                <tr><td colSpan={COLS} style={{ textAlign: 'center', padding: 24, color: BD.red }}>Não foi possível carregar a posição.</td></tr>
              )}
              {!isLoading && !isError && rows.length === 0 && (
                <tr><td colSpan={COLS} style={{ textAlign: 'center', padding: 24, color: BD.grayText }}>Nenhum item nesta praça com os filtros atuais.</td></tr>
              )}
              {!isLoading && !isError && rows.map(linha)}
            </tbody>
          </Table>
        </TabelaWrap>

        <ListaCartoes>
          {isLoading && <Card style={{ textAlign: 'center', padding: 24 }}><Spinner /></Card>}
          {!isLoading && isError && (
            <Card style={{ padding: 20, color: BD.red }}>Não foi possível carregar a posição.</Card>
          )}
          {!isLoading && !isError && rows.length === 0 && (
            <Card style={{ padding: 20, color: BD.grayText }}>Nenhum item nesta praça com os filtros atuais.</Card>
          )}
          {!isLoading && !isError && rows.map(cartao)}
        </ListaCartoes>
        <div style={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: 12, padding: 12 }}>
          <Button $variant="secondary" disabled={paginaAtual <= 1} onClick={() => irPagina(paginaAtual - 1)}>‹ Anterior</Button>
          <span style={{ fontSize: 13, color: BD.grayText }}>Página {paginaAtual}</span>
          <Button $variant="secondary" disabled={!!lastPage} onClick={() => irPagina(paginaAtual + 1)}>Próxima ›</Button>
        </div>
      </CaixaLista>

      <div style={{ marginTop: 8 }}>
        <AjudaGlossario termos={[...TERMOS]} />
      </div>
    </>
  )
}

export default EstoquePosicao
